import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Box, Typography } from '@mui/material';
import Counter from '../../components/common/Counter/Counter';
import CreatorDashboardListing from "../../components/single/CreatorDashboardListing/CreatorDashboardListing";
import PaginationTable from "../../components/common/PaginationTable/PaginationTable";
import PageSpinner from '../../components/common/PageSpinner/PageSpinner';
import { AiOutlineDollar, AiOutlineHeart, AiOutlineShoppingCart } from "react-icons/ai";
import { apiFetch } from '../../utils/api';
import styles from "./Dashboard.module.css";
import * as Sentry from "@sentry/react";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const columns = [
    { label: "Name", key: "name", width: "100%" },
    { label: "Price", key: "price", width: "200px" },
    { label: "Owners", key: "nrOwners", width: "130px" },
    { label: "Change", key: "prChange", width: "100px" },
];

const chartOptions = {
    responsive: true,
    plugins: {
        legend: { position: 'top' },
    }
};

const Dashboard = () => {
    const [ isPageLoading1, setIsPageLoading1 ] = useState(false);
    const [ isPageLoading2, setIsPageLoading2 ] = useState(false);
    const [data, setData] = useState([]);
    const [sortColumn, setSortColumn] = useState(null);
    const [sortOrder, setSortOrder] = useState("asc");
    const [timeRange, setTimeRange] = useState('all');

    const [sales, setSales] = useState(0);
    const [favorites, setFavorites] = useState(0);
    const [purchased, setPurchased] = useState(0);

    const [chartData, setChartData] = useState({ labels: [], datasets: [] });

    useEffect(() => {
        const fetchSummary = async () => {
            try {
                setIsPageLoading1(true);
                const startDate = "2025-04-01";

                const endDateObj = new Date();
                endDateObj.setHours(23, 59, 59, 999); // Set to today's end of day
                endDateObj.setDate(endDateObj.getDate() + 1); // Add 1 day

                const endDate = endDateObj.toISOString().split("T")[0];

                const res = await apiFetch('/api/dashboard/summary', {
                    method: "POST",
                    body: JSON.stringify({
                        startDate,
                        endDate
                    })
                }, true);
    
                setSales(res.sales || 0);
                setFavorites(res.views || 0);
                setPurchased(res.purchases || 0);
    
                setChartData({
                    labels: res.chart.map(entry => entry.date),
                    datasets: [
                        {
                            label: 'Views',
                            data: res.chart.map(entry => entry.views),
                            borderColor: '#8884d8',
                            backgroundColor: 'rgba(136, 132, 216, 0.2)',
                            fill: true,
                        },
                        {
                            label: 'Sales',
                            data: res.chart.map(entry => entry.sales),
                            borderColor: '#ff9500',
                            backgroundColor: 'rgba(255, 149, 0, 0.2)',
                            fill: true,
                        }
                    ]
                });
            } catch (err) {
                Sentry.captureException(err);
                console.error("❌ Failed to fetch dashboard summary:", err);
            } finally {
                setIsPageLoading1(false);
            }
        };
        fetchSummary();

        // setIsPageLoading(true);
    }, []);

    useEffect(() => {
        const fetchUserMemories = async () => {
            try {
                setIsPageLoading2(true);
                const res = await apiFetch("/api/memories/user", {}, true);
                const converted = (res || []).map(mem => ({
                    name: mem.name,
                    subtitle: mem.description.length > 64 ? mem.description.substring(0, 200) + "..." : mem.description,
                    img: mem.img,
                    nrOwners: mem.sales,
                    descriptor: mem.descriptor,
                    prChange: 0, // change percentage logic can be added if needed
                    price: mem.price,
                }));
                setData(converted);
            } catch (err) {
                Sentry.captureException(err);
                console.error("❌ Failed to fetch memories:", err);
            } finally {
                setIsPageLoading2(false);
            }
        };
    
        fetchUserMemories();
    }, []);

    const handleTimeRangeChange = (range) => {
        setTimeRange(range);
    };

    const filterData = (range) => {
        const weekDays = 7;
        const monthDays = 30;
    
        let filteredLabels, filteredViews, filteredSales;
    
        if (range === 'week') {
            filteredLabels = chartData.labels.slice(-weekDays);
            filteredViews = chartData.datasets[0]?.data.slice(-weekDays) || [];
            filteredSales = chartData.datasets[1]?.data.slice(-weekDays) || [];
        } else if (range === 'month') {
            filteredLabels = chartData.labels.slice(-monthDays);
            filteredViews = chartData.datasets[0]?.data.slice(-monthDays) || [];
            filteredSales = chartData.datasets[1]?.data.slice(-monthDays) || [];
        } else {
            filteredLabels = chartData.labels;
            filteredViews = chartData.datasets[0]?.data || [];
            filteredSales = chartData.datasets[1]?.data || [];
        }
    
        return {
            labels: filteredLabels,
            datasets: [
                { ...chartData.datasets[0], data: filteredViews },
                { ...chartData.datasets[1], data: filteredSales },
            ],
        };
    };

    const filteredData = filterData(timeRange);

    const handlePageChange = (newPage, sortColumn, sortOrder) => {
        console.log(`Fetching page ${newPage} sorted by ${sortColumn} (${sortOrder})`);
    };

    const handleSortChange = (column, order) => {
        setSortColumn(order ? column : null);
        setSortOrder(order || "asc");
    };


    if (isPageLoading1 || isPageLoading2) {
        return <PageSpinner />;
    }

    return (
        <Box className={styles.contentArea}>
            <Typography variant="h1" className={styles.dashboardTitle}>Dashboard</Typography>

            <Box className={styles.chartAndCounters}>

                <Box className={styles.counters}>
                    <Counter
                        icon={<AiOutlineHeart />}
                        title="Likes"
                        number={favorites.toLocaleString()}
                    />
                    <Counter
                        icon={<AiOutlineShoppingCart />}
                        title="Sales"
                        number={sales.toLocaleString()}
                    />
                    <Counter
                        icon={<AiOutlineDollar />}
                        title="Revenue"
                        number={`$${purchased.toLocaleString()}`}
                    />
                </Box>

                <Box className={styles.chartContainer}>
                    <Box className={styles.header}>
                        <Typography variant="h2" className={styles.salesHistoryTitle}>Sales History</Typography>
                        <Box className={styles.chartButtons}>
                            <span 
                                className={`${styles.chartButton} ${timeRange === 'week' ? styles.activeButton : ''}`}
                                onClick={() => handleTimeRangeChange('week')}
                            >
                                Week
                            </span>
                            <span 
                                className={`${styles.chartButton} ${timeRange === 'month' ? styles.activeButton : ''}`}
                                onClick={() => handleTimeRangeChange('month')}
                            >
                                Month
                            </span>
                            <span 
                                className={`${styles.chartButton} ${timeRange === 'all' ? styles.activeButton : ''}`}
                                onClick={() => handleTimeRangeChange('all')}
                            >
                                All
                            </span>
                        </Box>
                    </Box>
                    <hr className={styles.divider} />
                    <Line data={filteredData} options={chartOptions} />
                </Box>
            </Box>

            <Typography variant="h2" className={styles.bestsellersTitle}>Bestsellers</Typography>

            <PaginationTable 
                data={data} 
                columns={columns} 
                component={CreatorDashboardListing} 
                onPageChange={handlePageChange} 
                onSortChange={handleSortChange} 
                sortColumn={sortColumn}
                sortOrder={sortOrder}
            />
        </Box>
    );
};

export default Dashboard;
